import {
  classifySurface,
  normalizeError,
  type CrashEvent,
  type Session,
  type SupportedSurface,
} from "@panelgenie/domain";
import type {
  CrashReporter,
  ReportCrashInput,
  ReportCrashOutcome,
  StartSessionInput,
} from "./CrashReporter.js";

/**
 * Phase 14c — in-memory `CrashReporter` adapter for tests + SSR + the
 * no-DSN-configured local-dev arm.
 *
 * Stores events + sessions in memory; the test driver reads via
 * `getEvents()` / `getSessions()` to assert on what the consumer
 * shipped. The Phase 14c smoke-test script under
 * `apps/web/scripts/observability-smoke.mjs` runs against this
 * adapter and pins the wire shape.
 */
export type InMemoryCrashReporterOptions = {
  /**
   * Wall-clock instant generator; tests inject a deterministic
   * counter so `capturedAt` is reproducible.
   */
  readonly now?: () => string;
  /**
   * Synthetic event-id generator; tests inject a counter so
   * `eventId` is reproducible.
   */
  readonly nextEventId?: () => string;
  /**
   * Optional out-of-band session-storage source. The browser adapter
   * inherits the helper but does not retain events; the in-memory
   * adapter is the canonical "buffered" arm.
   */
  readonly retainEvents?: boolean;
};

export class InMemoryCrashReporter implements CrashReporter {
  private readonly events: CrashEvent[] = [];
  private readonly sessions: Session[] = [];
  private activeSession: Session | undefined;
  private readonly now: () => string;
  private readonly nextEventId: () => string;
  private readonly retainEvents: boolean;
  private eventCounter = 0;

  constructor(options: InMemoryCrashReporterOptions = {}) {
    this.now = options.now ?? (() => new Date().toISOString());
    this.retainEvents = options.retainEvents ?? true;
    this.nextEventId =
      options.nextEventId ??
      (() => {
        this.eventCounter += 1;
        return `event-${this.eventCounter}`;
      });
  }

  startSession(input: StartSessionInput): void {
    const session: Session = {
      sessionId: input.sessionId,
      startedAt: input.startedAt,
      crashedAt: undefined,
      surface: input.surface,
      release: input.release,
    };
    this.sessions.push(session);
    this.activeSession = session;
  }

  async reportCrash(input: ReportCrashInput): Promise<ReportCrashOutcome> {
    if (this.activeSession === undefined) {
      return {
        kind: "skipped",
        reason: "no active session — call startSession before reportCrash",
      };
    }
    const eventId = this.nextEventId();
    const capturedAt = this.now();
    const event: CrashEvent = {
      eventId,
      capturedAt,
      severity: input.severity,
      error: normalizeError(input.thrown),
      sessionId: this.activeSession.sessionId,
      release: this.activeSession.release,
      surface: this.activeSession.surface,
      userAgent: "in-memory",
      context: input.context ?? {},
    };
    // Mark the session as crashed on the first fatal / error event.
    // Subsequent events don't re-stamp `crashedAt` — the math counts
    // a crashed session as one boolean.
    if (
      (input.severity === "fatal" || input.severity === "error") &&
      this.activeSession.crashedAt === undefined
    ) {
      const replaced: Session = {
        ...this.activeSession,
        crashedAt: capturedAt,
      };
      const idx = this.sessions.indexOf(this.activeSession);
      if (idx >= 0) this.sessions[idx] = replaced;
      this.activeSession = replaced;
    }
    if (this.retainEvents) this.events.push(event);
    return { kind: "ok", eventId };
  }

  currentSessionId(): string | undefined {
    return this.activeSession?.sessionId;
  }

  /** Test helper — read the buffered events. */
  getEvents(): ReadonlyArray<CrashEvent> {
    return this.events;
  }

  /** Test helper — read the buffered sessions. */
  getSessions(): ReadonlyArray<Session> {
    return this.sessions;
  }
}

/**
 * Build a session-start input from a wall-clock instant + a user-
 * agent string. Pure helper. The browser adapter calls this with
 * `navigator.userAgent`; the in-memory adapter takes any string.
 */
export const buildStartSessionInput = ({
  sessionId,
  startedAt,
  release,
  userAgent,
}: {
  readonly sessionId: string;
  readonly startedAt: string;
  readonly release: string;
  readonly userAgent: string;
}): StartSessionInput => {
  const surface: SupportedSurface = classifySurface(userAgent);
  return {
    sessionId,
    startedAt,
    release,
    userAgent,
    surface,
  };
};
